import {
  ReturnsGranularity,
  useInvestmentReturnsMatrixQuery,
} from '@transactions/graphql';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {FilterDropdown} from 'src/components/FilterDropdown';
import type {AllInvestmentAccountsQuery} from '@transactions/graphql';

type MatrixAccount = AllInvestmentAccountsQuery['allInvestmentAccounts'][number];

type MatrixView = 'percent' | 'dollar';

// Cell tint steps for the diverging red–neutral–green ramp. In percent view
// the |return| thresholds scale with the period length — +8% is a mild year
// but a strong quarter — and in dollar view the same six alphas are driven
// by the cell's share of the largest |gain| in the matrix.
const ALPHA_STEPS = [0.04, 0.09, 0.16, 0.26, 0.38, 0.5];
const YEAR_THRESHOLDS = [0.02, 0.06, 0.12, 0.2, 0.3];
const PERCENT_THRESHOLDS: Record<ReturnsGranularity, number[]> = {
  [ReturnsGranularity.Year]: YEAR_THRESHOLDS,
  [ReturnsGranularity.Quarter]: YEAR_THRESHOLDS.map((t) => t / 2),
  [ReturnsGranularity.Month]: YEAR_THRESHOLDS.map((t) => t / 4),
};
const DOLLAR_THRESHOLDS = [0.02, 0.06, 0.15, 0.3, 0.6];

const steppedAlpha = (magnitude: number, thresholds: number[]): number => {
  let step = 0;
  while (step < thresholds.length && magnitude >= thresholds[step]) {
    step++;
  }
  return ALPHA_STEPS[step];
};

// Exactly-zero cells take the legend's neutral midpoint, not a green tint —
// a flat period is not a gain.
const cellBackground = (value: number, alpha: number): string =>
  value === 0
    ? 'var(--color-muted)'
    : value > 0
      ? `rgba(22, 163, 74, ${alpha})`
      : `rgba(220, 38, 38, ${alpha})`;

const formatPercentCell = (decimal: number): string => {
  const sign = decimal > 0 ? '+' : decimal < 0 ? '−' : '';
  return `${sign}${(Math.abs(decimal) * 100).toFixed(1)}`;
};

const compactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatDollarCell = (cents: number): string => {
  const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
  return `${sign}${compactCurrency.format(Math.abs(cents) / 100)}`;
};

// Column labels: the newest yearly column is the running year ("YTD");
// quarter and month keys compress to "Q3 26" / "Aug 26".
const periodLabel = (
  key: string,
  granularity: ReturnsGranularity,
  isNewest: boolean,
): string => {
  switch (granularity) {
    case ReturnsGranularity.Year:
      return isNewest ? 'YTD' : key;
    case ReturnsGranularity.Quarter: {
      const [year, quarter] = key.split('-');
      return `${quarter} ${year.slice(2)}`;
    }
    case ReturnsGranularity.Month: {
      const parsed = DateTime.fromFormat(key, 'yyyy-MM', {zone: 'utc'});
      return parsed.isValid ? parsed.toFormat('MMM yy') : key;
    }
  }
};

type ReturnsMatrixProps = {
  accounts: MatrixAccount[];
};

export const ReturnsMatrix = ({accounts}: ReturnsMatrixProps) => {
  const [granularity, setGranularity] = React.useState<ReturnsGranularity>(
    ReturnsGranularity.Year,
  );
  const [view, setView] = React.useState<MatrixView>('percent');

  const {data, loading, error} = useInvestmentReturnsMatrixQuery({
    variables: {granularity},
    fetchPolicy: 'cache-and-network',
  });

  const matrix = data?.investmentReturnsMatrix;

  const accountById = React.useMemo(
    () => new Map(accounts.map((account) => [account._id, account])),
    [accounts],
  );

  // Rows sort by the row's presentValue (the server's today-clamped value,
  // the same figure that decided eligibility) so the matrix reads
  // size-first; the balance bar under each name carries the same weight
  // context linearly. account.balance is deliberately not used — it is not
  // today-clamped, so a future-dated anchor would make it disagree with the
  // eligibility rule.
  const rows = React.useMemo(() => {
    if (matrix == null) return [];
    return matrix.rows
      .flatMap((row) => {
        const account = accountById.get(row.accountId);
        return account != null ? [{...row, account}] : [];
      })
      .sort((a, b) => b.presentValue - a.presentValue);
  }, [matrix, accountById]);

  if (matrix == null) {
    if (loading) {
      return (
        <div className="border-border bg-card flex h-40 items-center justify-center rounded-lg border">
          <span className="text-muted-foreground">Loading returns...</span>
        </div>
      );
    }
    // Settled without data: the request failed. Say so briefly rather than
    // spinning forever or silently dropping the card.
    if (error != null) {
      return (
        <div className="border-border bg-card flex h-20 items-center justify-center rounded-lg border">
          <span className="text-muted-foreground">
            Couldn&apos;t load returns.
          </span>
        </div>
      );
    }
    return null;
  }
  if (rows.length === 0 || matrix.periodKeys.length === 0) {
    // The server returns no rows when every investment account is presently
    // unfunded. The matrix owns the whole Performance tab, so say why the
    // tab is empty rather than rendering nothing.
    return (
      <div className="border-border bg-card flex h-24 items-center justify-center rounded-lg border">
        <span className="text-muted-foreground text-sm">
          No funded investment accounts to compare yet.
        </span>
      </div>
    );
  }

  const maxBalance = Math.max(...rows.map((row) => row.presentValue));
  const maxAbsGain = Math.max(
    1,
    ...rows.flatMap((row) =>
      row.periods.map((cell) => Math.abs(cell.gainAmount ?? 0)),
    ),
  );
  // The tally cells are different populations from the period cells (a span
  // total dwarfs any single period; the pooled row dwarfs any account), so
  // each population scales its dollar tints against its own maximum.
  const maxAbsRowTotal = Math.max(
    1,
    ...rows.map((row) => Math.abs(row.total.gainAmount ?? 0)),
  );
  const totals = matrix.totals;
  const maxAbsTotals = Math.max(
    1,
    ...(totals?.periods ?? []).map((cell) => Math.abs(cell.gainAmount ?? 0)),
    Math.abs(totals?.total.gainAmount ?? 0),
  );
  const percentThresholds = PERCENT_THRESHOLDS[granularity];

  // Legend bounds track the current scale: the outer swatches saturate at
  // the top tint threshold in percent view and at the matrix's largest
  // absolute gain in dollar view.
  const periodNoun =
    granularity === ReturnsGranularity.Year
      ? 'year'
      : granularity === ReturnsGranularity.Quarter
        ? 'quarter'
        : 'month';
  const topPercent = percentThresholds[percentThresholds.length - 1] * 100;
  const legendNegLabel =
    view === 'percent'
      ? `−${topPercent}%`
      : `−${compactCurrency.format(maxAbsGain / 100)}`;
  const legendPosLabel =
    view === 'percent'
      ? `+${topPercent}%`
      : `+${compactCurrency.format(maxAbsGain / 100)}`;
  const legendUnitLabel =
    view === 'percent'
      ? `return per ${periodNoun}, %`
      : `gain per ${periodNoun}, $`;

  const cellFor = (
    cell: {
      returnPercent?: number | null;
      gainAmount?: number | null;
      hasData: boolean;
    },
    dollarScale: number,
  ): {text: string; background: string | undefined; muted: boolean} => {
    if (!cell.hasData) {
      return {text: '—', background: undefined, muted: true};
    }
    if (view === 'percent') {
      if (cell.returnPercent == null) {
        return {text: '—', background: undefined, muted: true};
      }
      return {
        text: formatPercentCell(cell.returnPercent),
        background: cellBackground(
          cell.returnPercent,
          steppedAlpha(Math.abs(cell.returnPercent), percentThresholds),
        ),
        muted: false,
      };
    }
    if (cell.gainAmount == null) {
      return {text: '—', background: undefined, muted: true};
    }
    return {
      text: formatDollarCell(cell.gainAmount),
      background: cellBackground(
        cell.gainAmount,
        steppedAlpha(Math.abs(cell.gainAmount) / dollarScale, DOLLAR_THRESHOLDS),
      ),
      muted: false,
    };
  };

  const xirrCellFor = (
    xirr: number | null | undefined,
  ): {text: string; background: string | undefined; muted: boolean} =>
    xirr != null
      ? {
          text: formatPercentCell(xirr),
          background: cellBackground(
            xirr,
            steppedAlpha(Math.abs(xirr), YEAR_THRESHOLDS),
          ),
          muted: false,
        }
      : {text: '—', background: undefined, muted: true};

  return (
    <div className="border-border bg-card rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-1 gap-y-2">
        <h3 className="text-foreground mr-auto font-medium">
          Returns by account
        </h3>
        <FilterDropdown
          label="Scale"
          allLabel="Yearly"
          options={[
            {key: 'quarterly', label: 'Quarterly'},
            {key: 'monthly', label: 'Monthly'},
          ]}
          value={
            granularity === ReturnsGranularity.Year
              ? 'all'
              : granularity === ReturnsGranularity.Quarter
                ? 'quarterly'
                : 'monthly'
          }
          onChange={(key) =>
            setGranularity(
              key === 'quarterly'
                ? ReturnsGranularity.Quarter
                : key === 'monthly'
                  ? ReturnsGranularity.Month
                  : ReturnsGranularity.Year,
            )
          }
        />
        <FilterDropdown
          label="View"
          allLabel="% return"
          options={[{key: 'dollar', label: '$ gain'}]}
          value={view === 'percent' ? 'all' : 'dollar'}
          onChange={(key) => setView(key === 'dollar' ? 'dollar' : 'percent')}
        />
      </div>
      <div className="overflow-x-auto">
        <div
          className="grid gap-0.5"
          style={{
            gridTemplateColumns: `minmax(150px, 180px) 64px 68px repeat(${matrix.periodKeys.length}, minmax(56px, 1fr))`,
          }}
        >
          <div />
          <div className="text-muted-foreground flex h-7 items-center justify-center text-xs font-medium">
            XIRR
          </div>
          <div className="text-muted-foreground flex h-7 items-center justify-center text-xs font-medium">
            Total
          </div>
          {matrix.periodKeys.map((key, index) => (
            <div
              key={key}
              className="text-muted-foreground flex h-7 items-center justify-center text-xs"
            >
              {periodLabel(key, granularity, index === 0)}
            </div>
          ))}
          {rows.map((row) => {
            const xirrCell = xirrCellFor(row.xirr);
            const totalCell = cellFor(row.total, maxAbsRowTotal);
            return (
              <React.Fragment key={row.accountId}>
                <div className="flex min-w-0 flex-col justify-center gap-1 py-1 pr-2">
                  <Link
                    to={`/accounts/${row.accountId}`}
                    className="text-foreground truncate text-sm no-underline hover:underline"
                  >
                    {row.account.name}
                  </Link>
                  <div className="flex items-center gap-1.5">
                    <div className="bg-muted h-1 w-14 shrink-0 rounded-full">
                      <div
                        className="bg-muted-foreground/60 h-1 rounded-full"
                        style={{
                          width: `${Math.max(
                            (row.presentValue / maxBalance) * 100,
                            2,
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="text-muted-foreground text-[11px] whitespace-nowrap">
                      {compactCurrency.format(row.presentValue / 100)}
                    </span>
                  </div>
                </div>
                <div
                  className={`border-border flex h-full min-h-10 items-center justify-center rounded-md border text-xs ${
                    xirrCell.muted ? 'text-muted-foreground/60' : 'text-foreground'
                  }`}
                  style={{backgroundColor: xirrCell.background}}
                >
                  {xirrCell.text}
                </div>
                <div
                  className={`border-border flex h-full min-h-10 items-center justify-center rounded-md border text-xs ${
                    totalCell.muted ? 'text-muted-foreground/60' : 'text-foreground'
                  }`}
                  style={{backgroundColor: totalCell.background}}
                >
                  {totalCell.text}
                </div>
                {row.periods.map((cell) => {
                  const rendered = cellFor(cell, maxAbsGain);
                  return (
                    <div
                      key={cell.periodKey}
                      className={`flex h-full min-h-10 items-center justify-center rounded-md text-xs ${
                        rendered.muted
                          ? 'bg-muted/40 text-muted-foreground/60'
                          : 'text-foreground'
                      }`}
                      style={{backgroundColor: rendered.background}}
                    >
                      {rendered.text}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
          {totals != null && (
            <>
              {/* Pooled tallies: all displayed accounts as one portfolio. */}
              <div
                className="border-border mt-1 border-t pt-1"
                style={{gridColumn: '1 / -1'}}
              />
              <div className="flex min-w-0 flex-col justify-center gap-0.5 py-1 pr-2">
                <span className="text-foreground text-sm font-medium">
                  All accounts
                </span>
                <span className="text-muted-foreground text-[11px] whitespace-nowrap">
                  {compactCurrency.format(totals.presentValue / 100)}
                </span>
              </div>
              {(() => {
                const pooledXirr = xirrCellFor(totals.xirr);
                const grand = cellFor(totals.total, maxAbsTotals);
                return (
                  <>
                    <div
                      className={`border-border flex h-full min-h-10 items-center justify-center rounded-md border text-xs font-medium ${
                        pooledXirr.muted
                          ? 'text-muted-foreground/60'
                          : 'text-foreground'
                      }`}
                      style={{backgroundColor: pooledXirr.background}}
                    >
                      {pooledXirr.text}
                    </div>
                    <div
                      className={`border-border flex h-full min-h-10 items-center justify-center rounded-md border text-xs font-medium ${
                        grand.muted
                          ? 'text-muted-foreground/60'
                          : 'text-foreground'
                      }`}
                      style={{backgroundColor: grand.background}}
                    >
                      {grand.text}
                    </div>
                  </>
                );
              })()}
              {totals.periods.map((cell) => {
                const rendered = cellFor(cell, maxAbsTotals);
                return (
                  <div
                    key={cell.periodKey}
                    className={`flex h-full min-h-10 items-center justify-center rounded-md text-xs font-medium ${
                      rendered.muted
                        ? 'bg-muted/40 text-muted-foreground/60'
                        : 'text-foreground'
                    }`}
                    style={{backgroundColor: rendered.background}}
                  >
                    {rendered.text}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
      {/* The ramp legend carries the units — cells stay bare numbers for
          density, so this row is what says "percent" (or "dollars"). */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-muted-foreground text-[11px]">
          {legendNegLabel}
        </span>
        <span
          className="h-3 w-5 rounded-sm"
          style={{backgroundColor: 'rgba(220, 38, 38, 0.5)'}}
        />
        <span
          className="h-3 w-5 rounded-sm"
          style={{backgroundColor: 'rgba(220, 38, 38, 0.26)'}}
        />
        <span className="bg-muted h-3 w-5 rounded-sm" />
        <span
          className="h-3 w-5 rounded-sm"
          style={{backgroundColor: 'rgba(22, 163, 74, 0.26)'}}
        />
        <span
          className="h-3 w-5 rounded-sm"
          style={{backgroundColor: 'rgba(22, 163, 74, 0.5)'}}
        />
        <span className="text-muted-foreground text-[11px]">
          {legendPosLabel}
        </span>
        <span className="text-muted-foreground ml-auto text-[11px]">
          {legendUnitLabel}
        </span>
      </div>
    </div>
  );
};
